import type { AppSettings, AuthStatus, Chat, ChatDetail, MemoryFact, StreamEvent } from "./types";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const response = await fetch(`/api${path}`, { ...init, headers, credentials: "include" });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.detail ?? "Не удалось выполнить запрос");
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  authStatus: () => request<AuthStatus>("/auth/status"),
  setup: (password: string, setupToken = "") => request<{ recoveryToken: string; recoveryFile: string }>("/auth/setup", { method: "POST", body: JSON.stringify({ password, setup_token: setupToken || undefined }) }),
  login: (password: string) => request<{ authenticated: boolean }>("/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => request("/auth/logout", { method: "POST" }),
  recover: (recoveryToken: string, newPassword: string) => request("/auth/recover", { method: "POST", body: JSON.stringify({ recovery_token: recoveryToken, new_password: newPassword }) }),
  changePassword: (currentPassword: string, newPassword: string) => request("/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) }),
  chats: () => request<Chat[]>("/chats"),
  chat: (id: number) => request<ChatDetail>(`/chats/${id}`),
  createChat: () => request<Chat>("/chats", { method: "POST", body: JSON.stringify({ title: "Новый чат" }) }),
  renameChat: (id: number, title: string) => request<Chat>(`/chats/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  deleteChat: (id: number) => request<void>(`/chats/${id}`, { method: "DELETE" }),
  settings: () => request<AppSettings>("/settings"),
  saveSettings: (settings: Partial<AppSettings>) => request<AppSettings>("/settings", { method: "PUT", body: JSON.stringify(settings) }),
  memory: (search = "") => request<MemoryFact[]>(`/memory${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  createMemory: (content: string) => request<MemoryFact>("/memory", { method: "POST", body: JSON.stringify({ content }) }),
  updateMemory: (id: number, content: string) => request<MemoryFact>(`/memory/${id}`, { method: "PATCH", body: JSON.stringify({ content }) }),
  deleteMemory: (id: number) => request<void>(`/memory/${id}`, { method: "DELETE" }),
  markMemorySeen: () => request("/memory/mark-seen", { method: "POST" }),
  uploadFile: (chatId: number, file: File) => {
    const body = new FormData();
    body.append("upload", file);
    return request(`/chats/${chatId}/files`, { method: "POST", body });
  },
  transcribeVoice: (audio: Blob, filename: string) => {
    const body = new FormData();
    body.append("upload", audio, filename);
    return request<{ text: string; model: string }>("/voice/transcribe", { method: "POST", body });
  },
  deleteFile: (chatId: number, fileId: number) => request<void>(`/chats/${chatId}/files/${fileId}`, { method: "DELETE" }),
};

export async function streamRequest(
  path: string,
  body: Record<string, unknown> | undefined,
  onEvent: (event: StreamEvent) => void,
) {
  const response = await fetch(`/api${path}`, {
    method: "POST",
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok || !response.body) {
    const error = await response.json().catch(() => ({}));
    throw new ApiError(response.status, error.detail ?? "Не удалось получить ответ");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split("\n\n");
    buffer = blocks.pop() ?? "";
    for (const block of blocks) {
      let type = "message";
      let data = "{}";
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) type = line.slice(6).trim();
        if (line.startsWith("data:")) data = line.slice(5).trim();
      }
      onEvent({ type, data: JSON.parse(data) });
    }
    if (done) break;
  }
}
